import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Settings, Crown } from "lucide-react";
import { useLocation } from "wouter";
import { api } from "@/lib/api";

interface TeamInfo {
  id: string;
  name: string;
  member_count: number;
  subscription_status?: string;
  role: 'owner' | 'admin' | 'member';
}

export function TeamStatusCard() {
  const [, setLocation] = useLocation();
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeamInfo();
  }, []);

  const loadTeamInfo = async () => {
    try {
      const response = await api.get('/api/teams/my-team');
      if (response.data) {
        setTeam(response.data);
      }
    } catch (error) {
      // User doesn't have a team
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="animate-pulse bg-gray-200 rounded-lg w-8 h-8" />
            <div className="animate-pulse bg-gray-200 rounded w-24 h-6" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (!team) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Users className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-base">No Team Yet</CardTitle>
                <CardDescription className="text-sm">
                  Create a team to collaborate
                </CardDescription>
              </div>
            </div>
            <Button 
              size="sm" 
              onClick={() => setLocation('/team')}
            >
              <Plus className="h-4 w-4 mr-1" />
              Create Team
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{team.name}</CardTitle>
                {team.role === 'owner' && (
                  <Crown className="h-4 w-4 text-yellow-500" />
                )}
              </div>
              <CardDescription className="text-sm">
                {team.member_count} team members
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {team.subscription_status === 'active' ? (
              <Badge variant="default" className="bg-green-500">Active</Badge>
            ) : (
              <Badge variant="secondary">Free</Badge>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setLocation('/team')}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}